import { DashboardService } from '../../../../../service/events/dashboard.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseForm } from '../../../../base-form';
import { FertirrigacionFormDefinition } from './form-control/form-definition';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { ProductosApiService } from '../../../../../service/api/productos-api';
import {
  build,
  pool,
  ReactiveCascadeController,
  type ReactiveCascadeResetTarget,
  ReactiveDropdownSyncController,
} from '@fitogest/reactive-form-builder/utils';
import { TrabajadoresApiService } from '../../../../../service/api/trabajadores-api.service';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { HttpClient } from '@angular/common/http';

import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { SiexApiService } from '../../../../../service/api/siex-api.service';
import { FormPhase, ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import { FertirrigacionModel } from 'src/app/models/tareas/fertirrigacion.model';
import { AnalisisApiService } from 'src/app/service/api/analisis-api.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ComunidadesRegantesModel } from 'src/app/models/analisis/comunidadesRegantes.model';
import { StorageManager } from 'src/app/common/storage-manager.class';
import {
  ReactiveFormChangedField,
  ReactiveFormLifecycleEvent,
  ReactiveFormStableValueChange,
  ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';

type FertirrigacionDropdownCatalogPhase = 'critical' | 'deferred';

type FertirrigacionLocationCascadeField = 'ids_sectores' | 'ids_parcelas';

@Component({
  selector: 'app-fertirrigacion-form',
  standalone: false,
  templateUrl: './fertirrigacion-form.component.html',
  styleUrls: ['./fertirrigacion-form.component.scss'],
})
export class FertirrigacionFormComponent
  extends BaseForm<FertirrigacionModel>
  implements OnInit, OnDestroy {
  public loadingData = true;

  public isReactiveFormReady = false;

  public reactiveFormLoadingError = '';

  public readonly reactiveFormLoadingLabel = 'Preparando formulario de fertirrigación...';

  public reactiveFormRecordId = '';

  private readonly dropdownCatalogPhases: readonly FertirrigacionDropdownCatalogPhase[] = ['critical', 'deferred'];

  public comunidadForm: FormGroup;

  public form!: FertirrigacionFormDefinition;

  public formRequest: ReactiveFormRequestCoordinator<FertirrigacionModel>;

  public getRequest = this.tareasApi.fertirrigacion.GET;

  public initialPreviousData: FertirrigacionModel | null = null;

  constructor(
    public fincasApi: FincasApiService,
    public siexApi: SiexApiService,
    public tareasApi: TareasApiService,
    public productosApi: ProductosApiService,
    public trabajadoresApi: TrabajadoresApiService,
    public override router: Router,
    public override route: ActivatedRoute,
    public dashboardEvents: DashboardService,
    public clientesApi: ClientesApiService,
    public analisisApi: AnalisisApiService,
    private readonly http: HttpClient,
    private readonly formBuilder: FormBuilder
  ) {
    super(
      route,
      router,
      dashboardEvents,
      tareasApi.fertirrigacion.PUT,
      tareasApi.fertirrigacion.POST,
      'fertirrigacion',
      'Editar Riego',
      'Realizar Riego',
      'Duplicar Riego'
    );
    this.formRequest = new ReactiveFormRequestCoordinator<FertirrigacionModel>;

    this.comunidadForm = this.formBuilder.group({
      nombre: ['', [Validators.required, Validators.maxLength(100)]],
      id_usuario: [StorageManager.getUser().id],
      id_cliente: [StorageManager.getClient()?.id ?? ''],
    });
  }

  public async ngOnInit(): Promise<void> {
    const initialModel = this.initializeHostModel(this.getModel() as FertirrigacionModel);

    this.reactiveFormRecordId = this.resolveReactiveFormRecordId(initialModel);
    this.initialPreviousData = Object.keys(initialModel).length > 0 ? { ...initialModel } : null;
    this.form = new FertirrigacionFormDefinition(this.getType(), initialModel as Record<string, string>, null);
    this.form.model = this.toFertirrigacionModel({
      ...this.form.model,
      ...initialModel,
    });
    this.form.requestType = this.getType();
    this.softInit(this.getType());
    this.resetExternalDropdowns();
    this.formRequest.executePhase(FormPhase.LOAD);

    try {
      await this.initFormData();
      this.markReactiveFormReady();
    } catch (error: unknown) {
      this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
      console.error('No se ha podido preparar el formulario de fertirrigación.', error);
    }
  }

  public saveNewOption(): void {
    if (this.comunidadForm.invalid) {
      this.comunidadForm.markAllAsTouched();
      return;
    }

    const endpoint: string = `${this.form.env.serverUrl}/ws/index.php?p1=generic&p2=comunidades_regantes`;
    const payload: ComunidadesRegantesModel = this.comunidadForm.getRawValue();

    this.http.post<ComunidadesRegantesModel>(endpoint, payload)
      .subscribe({
        next: () => {
          this.comunidadForm.reset({
            nombre: '',
            id_usuario: StorageManager.getUser().id,
            id_cliente: StorageManager.getClient()?.id ?? '',
          });

          this.form.openDialog = false;

          this.loadDropdownCatalogField('id_comunidad_regantes').then(() => {
            const newName = payload.nombre;
            const match = this.form.lists.comunidadesRegantes.filtered
              .find((item: any) => item.value && item.value.nombre === newName);

            if (match) {
              const control = this.formBuilderRef?.model?.get('id_comunidad_regantes');

              if (control) {
                control.patchValue(match.value?.id, { emitEvent: false });
                control.markAsPristine();
              }
            }

            this.refreshReactiveDropdownMappings();
          });
        },
        error: (error: unknown) => {
          console.error('Error creating comunidad_regantes:', error);
        },
      });
  }

  public stableValueChanges(event: ReactiveFormStableValueChange<FertirrigacionModel>): void {
    const changedFieldName = String(event.changedField ?? '');
    const modelChanges = this.captureStableValueChange(event.value as FertirrigacionModel);

    this.form.model = this.toFertirrigacionModel({
      ...this.form.model,
      ...modelChanges,
    });
    this.syncModelFromForm();

    if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD) {
      this.applyLoadedReactiveState();
      return;
    }

    if (changedFieldName === DROPDOWN_REFRESH_FIELD) {
      this.syncModelFromForm();
      return;
    }

    if (event.origin !== 'user') {
      return;
    }

    this.handleFormValueChange(event.value, event.changedField);
  }

  public reactiveFormChanges(
    formModel: ReactiveFormValue<FertirrigacionModel>,
    lastChangedFieldName: ReactiveFormChangedField<FertirrigacionModel>,
  ): void {
    this.stableValueChanges({
      value: formModel,
      changedField: lastChangedFieldName,
      changedFields: [String(lastChangedFieldName ?? '')],
      origin: 'user',
    });
  }

  public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<FertirrigacionModel>): void {
    if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
      return;
    }

    const modelChanges = this.captureStableValueChange(event.value as FertirrigacionModel);

    this.form.model = this.toFertirrigacionModel({
      ...this.form.model,
      ...modelChanges,
    });
    this.syncModelFromForm();
    this.applyLoadedReactiveState();
  }

  public handleFormValueChange(
    formModel: ReactiveFormValue<FertirrigacionModel>,
    lastChangedFieldName: ReactiveFormChangedField<FertirrigacionModel>,
  ): void {
    const changedFieldName = String(lastChangedFieldName ?? '');

    this.form.model = this.toFertirrigacionModel({
      ...this.form.model,
      ...formModel,
    });
    this.form.tag = changedFieldName;
    this.syncModelFromForm();

    if (changedFieldName === 'id_finca') {
      this.clearLocationCascade(['ids_sectores', 'ids_parcelas']);
      this.refreshReactiveDropdownMappings();
      return;
    }

    if (changedFieldName === 'ids_sectores') {
      this.clearLocationCascade(['ids_parcelas']);
      this.refreshReactiveDropdownMappings();
    }
  }

  private async initFormData(): Promise<void> {
    this.loadingData = true;
    this.resetExternalDropdowns();

    try {
      await this.loadDropdownCatalogPhases();
      this.formRequest.executePhase(FormPhase.UPDATE);
      this.loadingData = false;
      this.refreshReactiveDropdownMappings();
    } catch (error: unknown) {
      this.loadingData = false;
      this.refreshReactiveDropdownMappings();
      console.error('Error loading fertirrigacion form data.', error);
      throw error;
    }
  }

  private async loadDropdownCatalogPhases(): Promise<void> {
    for (const phase of this.dropdownCatalogPhases) {
      await this.loadDropdownCatalogPhase(phase);
    }
  }

  private async loadDropdownCatalogPhase(phase: FertirrigacionDropdownCatalogPhase): Promise<void> {
    const catalogFields = this.form.formFields.dropdownCatalogs?.[phase] ?? [];
    const builds = catalogFields
      .map((fieldName) => this.createDropdownCatalogBuild(String(fieldName)))
      .filter((catalogBuild): catalogBuild is (resolve: (ok: boolean) => void) => void => catalogBuild !== null);

    if (builds.length === 0) {
      return;
    }

    await pool(builds, () => this.refreshReactiveDropdownMappings());
  }

  private loadDropdownCatalogField(fieldName: string): Promise<void> {
    const catalogBuild = this.createDropdownCatalogBuild(fieldName);

    if (catalogBuild === null) {
      return Promise.resolve();
    }

    return pool([catalogBuild], () => this.refreshReactiveDropdownMappings()).then(() => undefined);
  }

  private createDropdownCatalogBuild(fieldName: string): ((resolve: (ok: boolean) => void) => void) | null {
    if (fieldName === 'id_finca' || fieldName === 'finca') {
      return build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true);
    }

    if (fieldName === 'ids_sectores' || fieldName === 'sectores_nombres') {
      return build(this.fincasApi.sectoresGET, this.form.lists.sectores, 'nombre', true);
    }

    if (fieldName === 'ids_parcelas' || fieldName === 'parcelas_nombres') {
      return build(
        this.fincasApi.parcelasGET,
        this.form.lists.parcelas,
        this.form.env.appName === 'avafitogest' ? this.parcelaLabel : 'nombre',
        true
      );
    }

    if (fieldName === 'cultivo') {
      return build(this.siexApi.cultivosGET, this.form.lists.cultivos, 'cultivo', true);
    }

    if (fieldName === 'sistema_riego') {
      return build(this.siexApi.sistemasRiegoGET, this.form.lists.sistemasRiego, 'sistema_de_riego');
    }

    if (fieldName === 'id_comunidad_regantes') {
      return build(this.analisisApi.comunidadesRegantes.GET, this.form.lists.comunidadesRegantes, 'nombre');
    }

    if (fieldName === 'id_maquinaria') {
      return build(this.trabajadoresApi.maquinariaGET, this.form.lists.maquinarias, 'nombre');
    }

    return null;
  }

  private clearLocationCascade(fieldNames: readonly FertirrigacionLocationCascadeField[]): void {
    const resetTargets = fieldNames.map((fieldName): ReactiveCascadeResetTarget<FertirrigacionModel> => ({
      fieldName,
      value: '',
    }));
    const { updates } = ReactiveCascadeController.createResetPatch<FertirrigacionModel>(resetTargets);

    this.form.model = this.toFertirrigacionModel({
      ...this.form.model,
      ...updates,
    });
    this.syncModelFromForm();
    this.formBuilderRef?.updateForm(updates, false);
  }


  private syncModelFromForm(): void {
    this.updateHostModel(this.toFertirrigacionModel(this.form.model));
  }

  private toFertirrigacionModel(
    sourceModel: ReactiveFormValue<FertirrigacionModel> | Partial<FertirrigacionModel> | Record<string, unknown>,
  ): FertirrigacionModel {
    const sanitizedModel: Record<string, unknown> = {};

    Object.entries(sourceModel).forEach(([key, value]) => {
      if (value !== undefined) {
        sanitizedModel[key] = value;
      }
    });

    return sanitizedModel as FertirrigacionModel;
  }

  private refreshReactiveDropdownMappings(): void {
    ReactiveDropdownSyncController.refreshMappings({
      formBuilder: this.formBuilderRef,
      beforeRefresh: () => this.finalizeExternalDropdowns(),
    });
  }

  private applyLoadedReactiveState(): void {
    this.form.model = this.toFertirrigacionModel({
      ...this.form.model,
      ...(this.initialPreviousData ?? this.model),
    });
    this.syncModelFromForm();
    this.refreshReactiveDropdownMappings();
  }

  private markReactiveFormReady(): void {
    this.isReactiveFormReady = true;
    this.finalizeExternalDropdowns();
    this.refreshReactiveDropdownMappings();
  }

  private getReadableInitialisationError(error: unknown): string {
    if (error instanceof Error && error.message.trim() !== '') {
      return 'No se ha podido cargar el formulario de fertirrigación: ' + error.message;
    }

    return 'No se ha podido cargar el formulario de fertirrigación. Revisa la conexión e inténtalo de nuevo.';
  }

  private readonly parcelaLabel = (p: ParcelasModel): string => {
    const nombre = (p.nombre ?? '').toString().trim();
    const variedad = (p.variedad ?? 'SIN VARIEDAD').toString().trim();

    let zonaRiegoAbrev = '';

    if ((p.cultivo ?? '').toLowerCase() === 'arroz') {
      const zr = (p.zona_riego ?? '').toString().toLowerCase();

      if (zr.includes('alta')) { zonaRiegoAbrev = 'ZA'; }
      else if (zr.includes('baja')) { zonaRiegoAbrev = 'ZB'; }
    }

    const variedadConZona = zonaRiegoAbrev
      ? `${variedad} - ${zonaRiegoAbrev}`
      : variedad;

    const pol = (p.poligono ?? '').toString().trim();
    const num = (p.numero ?? '').toString().trim();
    const rec = (p.recinto ?? '').toString().trim();
    const codigos = [pol, num, rec].filter(Boolean).join('-');

    const supRaw = (p.superficie_cultivada ?? p.superficie_sigpac ?? p.superficie ?? '0').toString();
    const supNum = parseFloat(supRaw.replace(',', '.')) || 0;
    const supFmt = supNum.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    const uds = this.form.env.textoSuperficie || 'ha';

    const derecha = [
      codigos || undefined,
      `${supFmt} ${uds}`
    ].filter(Boolean).join(' · ');

    return `${nombre} - ${variedadConZona} — ${derecha}`;
  };
}
